import { FC, useId } from 'react';
import * as DropDownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faArrowUpArrowDown } from '@fortawesome/pro-regular-svg-icons/faArrowUpArrowDown';
import {
  cn,
  DropdownMenu,
  FaIcon,
  IDropdownMenuItemComponent,
  IMenuItem,
} from '@fe-learner-common';
import { A11yButton } from '@fe-core-ui/src/components/A11yButton';
import { Spinner } from '@fe-core-ui/Spinner';

export type TSortOption = {
  key: string;
  label: string;
};

export interface SorterProps {
  items: TSortOption[];
  selectedSortOrderKey: string;
  onSelect: (sortOrder: TSortOption) => void;
  onOpen?: (open: boolean) => void;
  triggerSize?: 'sm' | 'md' | 'lg';
  container: HTMLElement;
  resultsLoading?: boolean;
}

interface MenuItemProps extends IDropdownMenuItemComponent {
  label: string;
  checked: boolean;
  onCheckedChange: () => void;
}

const triggerSizeMap = {
  sm: 'l-btn-sm',
  md: 'l-btn-md',
  lg: 'l-btn-lg',
};

const MenuItem: FC<MenuItemProps> = ({
  className,
  label,
  checked,
  onCheckedChange,
}) => (
  <DropDownMenuPrimitive.CheckboxItem
    className={cn(
      className,
      'flex items-center justify-between gap-5 data-[state=checked]:pe-3',
    )}
    checked={checked}
    onCheckedChange={onCheckedChange}
  >
    <p className="text-title-sm">{label}</p>
    <DropDownMenuPrimitive.ItemIndicator>
      <FaIcon
        className="text-prominence-500"
        icon={faCheck}
        width={16}
        height={16}
      />
    </DropDownMenuPrimitive.ItemIndicator>
  </DropDownMenuPrimitive.CheckboxItem>
);

export const Sorter: FC<SorterProps> = ({
  items,
  selectedSortOrderKey,
  onSelect,
  onOpen,
  triggerSize = 'md',
  container,
  resultsLoading = false,
}) => {
  const spinnerId = useId();

  const selectedSortOrder = items.find((x) => x.key === selectedSortOrderKey);
  const menuItems: IMenuItem[] = items.map((item) => ({
    key: item.key,
    Component: (props) => (
      <MenuItem
        {...props}
        label={item.label}
        checked={selectedSortOrderKey === item.key}
        onCheckedChange={() => onSelect(item)}
      />
    ),
  }));

  return (
    <DropdownMenu
      items={menuItems}
      container={container}
      onOpenChange={(open) => {
        onOpen?.(open);
      }}
      trigger={
        <A11yButton
          disabled={resultsLoading}
          className={cn('l-btn l-btn-tertiary', triggerSizeMap[triggerSize])}
        >
          <span className="hidden md:inline-block">
            {selectedSortOrder?.label}
          </span>
          <span className="flex h-6 w-6 items-center justify-center">
            {resultsLoading ? (
              <Spinner name={spinnerId} />
            ) : (
              <>
                <FaIcon
                  className="hidden md:inline-block"
                  icon={faChevronDown}
                  width={16}
                  height={16}
                />
                <FaIcon
                  className="md:hidden"
                  icon={faArrowUpArrowDown}
                  width={16}
                  height={16}
                />
              </>
            )}
          </span>
        </A11yButton>
      }
    />
  );
};
